import React, { useState, useCallback, useMemo } from "react";
import { Modal } from "react-bootstrap";
import SearchColumn from "./modal/SearchColumn";
import ResultsColumn from "./modal/ResultsColumn";
import ActionBar from "./modal/ActionBar";
import DetailPane from "./shared/DetailPane";
import useSearchResults from "./useSearchResults";
import useCandidatePool from "./useCandidatePool";
import useVerdictBatching from "./useVerdictBatching";
import digestStoryIdea from "./verdictDigest";
import { SEARCH_MODES, DEFAULT_LOGIN_RECENCY } from "./constants";

const POST_FILTER_DEFAULT_MAX_DISTANCE = 0.85;
const HIDE_EXISTING_STORAGE_KEY = "recipientPicker.hideExisting";

function loadInitialHideExisting() {
  if (typeof window === "undefined" || !window.localStorage) return false;
  try {
    return window.localStorage.getItem(HIDE_EXISTING_STORAGE_KEY) === "true";
  } catch (_) {
    return false;
  }
}

function recencyToDays(value) {
  switch (value) {
    case "1month":
      return 30;
    case "3months":
      return 90;
    case "6months":
      return 180;
    default:
      return null;
  }
}

// Build a per-mode state record { semantic, by_name, by_outlet }.
const perMode = (value) => SEARCH_MODES.reduce((acc, m) => ({ ...acc, [m]: value }), {});

// Single-stage modal picker. Each session is one search → check rows →
// "Add selected" → save & close. Cross-search accumulation and the old
// Stage 2 review/remove surface are gone — removal + grouped review
// happen on the form's RecipientSummary instead.
//
// Source-group provenance is still preserved: each "Add selected" call
// tags the added rows with the source that produced them, and the
// merged { ids, groups } flushes through onSave.
//
// Per-mode state: each tab keeps its own query, results, checked set,
// focused row, and post-filter settings (max_distance, login_recency,
// hide-existing). Switching tabs swaps the active mode's projection —
// admins can run Smart-match, jump to By-name, then come back to
// Smart-match and find their query + results + selection intact. The
// modal's initial centered/larger layout only applies until the first
// search runs in any tab (hasEverSearched); after that the layout
// stays in standard form across tab switches.
//
// "Add selected" still commits only the active tab's selections. If
// admins want to commit multiple tabs' picks, they Add → reopen.
//
// Edit-mode hydration: pass initialIds + initialGroups; useCandidatePool
// hydrates from admin_recipients_lookup, synthesizing stub rows for any
// missing UUIDs. Hydrated rows are marked "already added" in the results
// column so the admin can see who's already on the list without picking
// them twice.
//
// Props:
//   show               — modal visibility (controlled by parent)
//   onClose            — called when admin closes the modal (Save or Cancel/X)
//   onSave({ids,groups,candidates}) — called when admin presses "Add selected";
//                        parent flushes through to formik
//   initialIds         — already-selected recipient IDs (edit mode)
//   initialGroups      — already-selected groups (edit mode)
//   initialCandidates  — already-selected candidate objects with display
//                        details (in-session reopen case; preferred over
//                        a PostgREST round-trip)
export default function RecipientPicker({
  show,
  onClose,
  onSave,
  initialIds = [],
  initialGroups = [],
  initialCandidates = [],
  // AI vet (relocated from the form footer). The picker owns the vet
  // action via useVerdictBatching; verdicts flush back to the form
  // through onVerdictsChange. `verdicts` is the current hash (drives the
  // call-site cache + card decoration); `storyIdea` is the prompt-inputs.
  // Staleness is per-verdict: each verdict carries the digest it was
  // vetted against, compared against the current inputs' digest.
  verdicts = {},
  storyIdea = null,
  onVerdictsChange = () => {},
  // Notification context for the eligibility decoration on each
  // candidate. Threaded down to useCandidatePool's fetcher which
  // calls admin_user_notification_eligibility with this db_name.
  // Defaults to the StoryIdea use case since that's the only
  // consumer today; future pickers for other notifications should
  // pass their own db_name explicitly.
  notificationDbName = "media_new_story_idea",
}) {
  // Active mode tab.
  const [mode, setMode] = useState(SEARCH_MODES[0]);

  // One useSearchResults instance per mode — switching tabs swaps which
  // is "active". Each maintains its own query, results, checked,
  // loading, error, and abort handle in place.
  const searchSemantic = useSearchResults();
  const searchByName = useSearchResults();
  const searchByOutlet = useSearchResults();
  const searches = useMemo(
    () => ({ semantic: searchSemantic, by_name: searchByName, by_outlet: searchByOutlet }),
    [searchSemantic, searchByName, searchByOutlet],
  );
  const search = searches[mode];

  // Per-mode UI state.
  const [searchedByMode, setSearchedByMode] = useState(perMode(false));
  const [focusedByMode, setFocusedByMode] = useState(perMode(null));
  const [maxDistanceByMode, setMaxDistanceByMode] = useState(perMode(POST_FILTER_DEFAULT_MAX_DISTANCE));
  const [loginRecencyByMode, setLoginRecencyByMode] = useState(perMode(DEFAULT_LOGIN_RECENCY));
  const [hideExistingByMode, setHideExistingByMode] = useState(() => perMode(loadInitialHideExisting()));

  // Active-mode projections.
  const searched = searchedByMode[mode];
  const focused = focusedByMode[mode];
  const maxDistance = maxDistanceByMode[mode];
  const loginRecency = loginRecencyByMode[mode];
  const hideExisting = hideExistingByMode[mode];

  // Once any tab has run a search we leave the initial centered/larger
  // layout for the rest of the session. Tab switches keep the standard
  // search-bar-at-top layout even if the destination tab has no prior
  // search (it'll render its own per-mode pre-search empty state).
  const hasEverSearched = SEARCH_MODES.some((m) => searchedByMode[m]);

  // Per-mode setters — close over `mode` at call time so they always
  // affect the active tab.
  const setSearched = useCallback((val) => setSearchedByMode((prev) => ({ ...prev, [mode]: val })), [mode]);
  const setFocused = useCallback((val) => setFocusedByMode((prev) => ({ ...prev, [mode]: val })), [mode]);
  const setMaxDistance = useCallback((val) => setMaxDistanceByMode((prev) => ({ ...prev, [mode]: val })), [mode]);
  const setLoginRecency = useCallback((val) => setLoginRecencyByMode((prev) => ({ ...prev, [mode]: val })), [mode]);
  const setHideExisting = useCallback(
    (val) => {
      // hide-existing is also persisted globally — admin's preference
      // carries across modal sessions.
      setHideExistingByMode((prev) => ({ ...prev, [mode]: val }));
      try {
        window.localStorage?.setItem(HIDE_EXISTING_STORAGE_KEY, String(val));
      } catch (_) {
        // localStorage unavailable.
      }
    },
    [mode],
  );

  // Pool — internal accumulator. Used to merge new selections with the
  // already-saved set so the modal can flush the merged result on save.
  // Not visualised; the form's RecipientSummary handles the visible
  // grouped review.
  const pool = useCandidatePool({ initialIds, initialGroups, initialCandidates, notificationDbName });

  // Set of ids that are already in the pool (from previous saves or
  // hydration). Drives the "Already added" badge on result rows.
  const existingIds = useMemo(() => new Set(pool.pool.map((c) => c.id)), [pool.pool]);

  // AI vet — owned here now (relocated from the form footer). The hook
  // batches the call and stamps each returned verdict with the current
  // prompt-inputs digest; verdicts flush back to formik via
  // onVerdictsChange as they land.
  const {
    vet,
    cancel: cancelVet,
    vetting,
    progress: vetProgress,
    failedBatches: vetFailedBatches,
  } = useVerdictBatching({ storyIdea, verdicts, onVerdictsChange });

  // The current prompt-inputs digest. ResultsColumn compares each
  // verdict's stored `vetted_against` to this to decide, per-verdict,
  // whether it's stale (the admin edited title/news_hook/topics since
  // that verdict was generated).
  const currentDigest = useMemo(() => digestStoryIdea(storyIdea), [storyIdea]);

  // Note: there's no in-picker "story described" gate on the vet button.
  // Recipient picking is gated at the form (RecipientsField `locked`) on
  // title + news_hook, so the picker can only open once the story is
  // described — the vet always has context. The server still validates
  // (Admin::Api::RecipientVettingsController#story_idea_valid?) as a backstop.

  // Single source of truth for "does this row pass the current
  // post-filters". Used by both the count of selected-and-visible
  // (here, drives the Add button) and the rendering filter inside
  // ResultsColumn.
  const rowPasses = useCallback(
    (row) => {
      if (hideExisting && existingIds.has(row.id)) return false;
      if (mode === "semantic" && row.semantic_distance != null && row.semantic_distance > maxDistance) return false;
      const days = recencyToDays(loginRecency);
      if (days) {
        if (!row.last_active_at) return false;
        const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
        if (new Date(row.last_active_at).getTime() < cutoff) return false;
      }
      return true;
    },
    [mode, maxDistance, loginRecency, hideExisting, existingIds],
  );

  const handleRunSearch = useCallback(async () => {
    // Flip searched BEFORE awaiting the API call so the layout-collapse
    // animation runs while loading instead of after the results arrive
    // — admins see the modal respond immediately on click. (Only on the
    // first-ever search does the centered layout actually collapse;
    // subsequent searches re-set the already-true flag.)
    setSearched(true);
    // Reset the detail-pane focus so the previously-selected row from
    // an earlier search doesn't linger after the new results render.
    setFocused(null);
    await search.runSearch({ mode });
  }, [search, mode, setSearched, setFocused]);

  const buildSourceForCurrentSearch = useCallback(() => {
    if (mode === "semantic") {
      return { type: "semantic", params: { query: search.query } };
    }
    if (mode === "by_name") {
      return { type: "by_name", params: { query: search.query } };
    }
    const outletNames = (search.results.rows || []).map((g) => g.outlet?.name).filter(Boolean);
    return { type: "by_outlet", params: { query: search.query, outlet_names: outletNames } };
  }, [mode, search.query, search.results]);

  const checkedRowsForAdd = search.checkedRows();
  // Add count = checked AND not already on the list AND currently visible
  // AND deduped by id. by_outlet can return the same reporter under
  // multiple outlet groups (cross-org employment); the count + the
  // commit treats each user once. Filter visibility means hidden checked
  // rows are excluded from the add, but their checked state is preserved
  // so loosening the filter brings them back.
  const addableRowsForAdd = useMemo(() => {
    const seen = new Set();
    return checkedRowsForAdd.filter((r) => {
      if (seen.has(r.id)) return false;
      if (existingIds.has(r.id)) return false;
      if (!rowPasses(r)) return false;
      seen.add(r.id);
      return true;
    });
  }, [checkedRowsForAdd, existingIds, rowPasses]);

  // Merge previously-hydrated pool with newly-checked rows, group by
  // source, and flush. Inline merge (vs calling pool.addBatch then
  // pool.toFormData) keeps the side-effect-free path: the modal
  // doesn't need to wait for setState to propagate before closing, and
  // useCandidatePool's internal state isn't read again after this call.
  const handleAddSelectedAndClose = useCallback(() => {
    const source = buildSourceForCurrentSearch();
    const merged = [...pool.pool];
    addableRowsForAdd.forEach((r) => {
      if (!merged.find((c) => c.id === r.id)) merged.push({ ...r, source });
    });

    const ids = merged.map((c) => c.id);
    const groupsByKey = new Map();
    merged.forEach((c) => {
      const key = JSON.stringify(c.source);
      if (!groupsByKey.has(key)) {
        groupsByKey.set(key, {
          source_type: c.source?.type || "unknown",
          source_params: c.source?.params || {},
          user_ids: [],
        });
      }
      groupsByKey.get(key).user_ids.push(c.id);
    });

    onSave({ ids, groups: Array.from(groupsByKey.values()), candidates: merged });
    onClose();
  }, [addableRowsForAdd, pool.pool, buildSourceForCurrentSearch, onSave, onClose]);

  return (
    <Modal show={show} onHide={onClose} size="xl" fullscreen="lg-down">
      {/* Local style hatch: react-bootstrap's <Nav variant="tabs">
          paints the active tab white via .nav-tabs .nav-link.active.
          Scope a tint override to .recipient-picker-tabs so the active
          tab blends into the bg-body-secondary surface around it,
          rather than punching through with white. */}
      <style>{`
        .recipient-picker-tabs .nav-link.active,
        .recipient-picker-tabs .nav-link.active:focus,
        .recipient-picker-tabs .nav-link.active:hover {
          background-color: rgb(53 98 135 / 4%);
        }
        /* Initial state: search region vertically + horizontally centered,
           70% of modal width, slightly bigger inputs. After first search
           the .searched class slides everything back to full-width / top
           via padding + width transitions; results fade in below. */
        .recipient-picker-search-region {
          display: flex;
          flex-direction: column;
          align-items: center;
          transition: padding 400ms ease;
        }
        .recipient-picker-search-region.initial {
          flex: 1 1 auto;
          justify-content: center;
        }
        .recipient-picker-search-region.searched {
          flex: 0 0 auto;
          padding: 0;
        }
        .recipient-picker-search-inner {
          transition: width 400ms ease;
        }
        .recipient-picker-search-region.initial .recipient-picker-search-inner {
          width: 70%;
        }
        .recipient-picker-search-region.searched .recipient-picker-search-inner {
          width: 100%;
        }
        .recipient-picker-search-region .form-control,
        .recipient-picker-search-region .btn {
          transition: font-size 250ms ease, padding 250ms ease;
        }
        .recipient-picker-search-region.initial .form-control,
        .recipient-picker-search-region.initial .btn {
          font-size: 1.15rem;
          padding-top: 0.65rem;
          padding-bottom: 0.65rem;
        }
        @keyframes recipient-picker-fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        .recipient-picker-results-region {
          animation: recipient-picker-fade-in 350ms ease;
        }
      `}</style>
      <Modal.Header closeButton className="bg-body-secondary">
        <Modal.Title className="d-flex align-items-center gap-3">
          <span>Pick recipients</span>
          {existingIds.size > 0 && <small className="text-muted">{existingIds.size} already on list</small>}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-0 d-flex flex-column bg-body-secondary" style={{ height: "75vh" }}>
        {/* Initial state: search bar centered (70% wide) + larger.
            Centered layout only applies until any tab has been searched;
            after that, tab switches keep the standard top-of-modal
            layout regardless of whether the active tab has its own
            search yet. */}
        <div className={`recipient-picker-search-region ${hasEverSearched ? "searched" : "initial"}`}>
          <div className="recipient-picker-search-inner">
            <SearchColumn
              mode={mode}
              setMode={setMode}
              query={search.query}
              setQuery={search.setQuery}
              onSearch={handleRunSearch}
              loading={search.loading}
            />
          </div>
        </div>

        {hasEverSearched && (
          <div className="d-flex flex-grow-1 recipient-picker-results-region" style={{ minHeight: 0 }}>
            <div className="border-end flex-grow-1 d-flex flex-column" style={{ width: "65%" }}>
              <ResultsColumn
                mode={mode}
                results={search.results}
                checked={search.checked}
                toggleCheck={search.toggleCheck}
                setCheckedForIds={search.setCheckedForIds}
                loading={search.loading}
                error={search.error}
                retryAdvised={search.retryAdvised}
                onRetry={handleRunSearch}
                focusedId={focused?.id}
                onFocus={setFocused}
                searched={searched}
                existingIds={existingIds}
                addableSelectedCount={addableRowsForAdd.length}
                maxDistance={maxDistance}
                setMaxDistance={setMaxDistance}
                loginRecency={loginRecency}
                setLoginRecency={setLoginRecency}
                hideExisting={hideExisting}
                setHideExisting={setHideExisting}
                rowPasses={rowPasses}
                verdicts={verdicts}
                currentDigest={currentDigest}
                onVetVisible={vet}
                onCancelVet={cancelVet}
                vetting={vetting}
                vetProgress={vetProgress}
                vetFailedBatches={vetFailedBatches}
              />
            </div>
            <div style={{ width: "35%", overflowY: "auto" }}>
              <DetailPane candidate={focused} />
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="p-0 bg-body-secondary">
        <ActionBar
          addableCount={addableRowsForAdd.length}
          onAddSelected={handleAddSelectedAndClose}
          onCancel={onClose}
        />
      </Modal.Footer>
    </Modal>
  );
}
